import React, { useState } from "react";
import { Helmet } from "react-helmet";
import "../css/dns-records.css";

// Icons
import { 
  FaGlobe,
  FaSearch,
  FaServer,
  FaCopy,
  FaCheck,
  FaSpinner,
  FaExclamationTriangle,
  FaShieldAlt,
  FaDatabase,
  FaEnvelope,
  FaLink
} from "react-icons/fa";

const DNSRecords = () => {
  const [domain, setDomain] = useState("");
  const [dnsData, setDnsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState("");

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!domain.trim()) {
      setError("Please enter a domain name");
      return;
    }

    // Clean domain input
    const cleanDomain = domain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
    
    try {
      setIsLoading(true);
      setError(null);
      setDnsData(null);

      // Use Google's DNS-over-HTTPS API instead
      const dnsTypes = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS'];
      const dnsPromises = dnsTypes.map(async (type) => {
        try {
          const response = await fetch(`https://dns.google/resolve?name=${cleanDomain}&type=${type}`, {
            headers: {
              'Accept': 'application/json',
            },
          });
          const data = await response.json();
          return { type, data: data.Answer || [] };
        } catch (error) {
          console.error(`Error fetching ${type} records:`, error);
          return { type, data: [] };
        }
      });

      const results = await Promise.all(dnsPromises);
      
      // Process and organize DNS data
      const processedData = {
        domain: cleanDomain,
        records: {}
      };

      results.forEach(({ type, data }) => {
        processedData.records[type] = data.map(record => ({
          name: record.name,
          type: record.type,
          data: record.data,
          ttl: record.TTL
        }));
      });

      setDnsData(processedData);

    } catch (error) {
      console.error('DNS lookup error:', error);
      setError('Unable to fetch DNS records. Please check the domain name and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text, recordType) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(recordType);
      setTimeout(() => setCopied(""), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(recordType);
      setTimeout(() => setCopied(""), 2000);
    });
  };

  const getRecordIcon = (type) => {
    switch (type) {
      case 'A':
      case 'AAAA':
        return <FaServer className="record-icon" />;
      case 'MX':
        return <FaEnvelope className="record-icon" />;
      case 'CNAME':
        return <FaLink className="record-icon" />;
      case 'TXT':
        return <FaShieldAlt className="record-icon" />;
      case 'NS':
        return <FaDatabase className="record-icon" />;
      default:
        return <FaGlobe className="record-icon" />;
    }
  };

  return (
    <div className="dns-records-page">
      <Helmet>
        <title>DNS Records Lookup - Check Domain DNS Records | ClubHosty</title>
        <meta
          name="description"
          content="Free DNS records lookup tool. Check A, AAAA, CNAME, MX, TXT, NS records for any domain. View complete DNS configuration instantly."
        />
        <link rel="canonical" href="https://www.clubhosty.com/dns-records" />
      </Helmet>

      {/* Hero Section - Minimal & Modern Design */}
      <section className="dns-hero-minimal">
        <div className="container">
          <div className="hero-content-minimal">
            <div className="hero-text-section">
              <h1 className="hero-title-minimal">
                DNS Records Lookup
              </h1>
              <p className="hero-desc-minimal">
                Check complete DNS configuration for any domain instantly
              </p>
            </div>

            {/* Search Form - Minimal Design */}
            <div className="dns-search-minimal">
              <form onSubmit={handleSearch} className="search-form-minimal">
                <div className="search-input-minimal">
                  <FaGlobe className="input-icon-minimal" />
                  <input
                    type="text"
                    placeholder="Enter domain name (e.g., example.com)"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    className="domain-input-minimal"
                  />
                  <button 
                    type="submit" 
                    className="search-btn-minimal"
                    disabled={isLoading}
                  >
                    {isLoading ? <FaSpinner className="spinner" /> : <FaSearch />}
                    {isLoading ? 'Searching...' : 'Lookup'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        {copied && (
          <div className="toast-notification">
            <FaCheck />
            <span>Copied!</span>
          </div>
        )}
      </section>

      {/* Error Section - Minimal */}
      {error && (
        <section className="error-section-minimal">
          <div className="container">
            <div className="error-card-minimal">
              <div className="error-content-minimal">
                <FaExclamationTriangle className="error-icon-minimal" />
                <p>{error}</p>
                <button onClick={() => setError(null)} className="retry-btn-minimal">
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* DNS Results - Minimal Design */}
      {dnsData && (
        <section className="dns-results-minimal">
          <div className="container">
            <div className="results-header-minimal">
              <h2>DNS Records for <span className="domain-name-minimal">{dnsData.domain}</span></h2>
            </div>

            <div className="dns-records-grid-minimal">
              {Object.entries(dnsData.records).map(([recordType, records]) => {
                if (records.length === 0) return null;
                
                return (
                  <div key={recordType} className="dns-record-card-minimal">
                    <div className="record-header-minimal">
                      <div className="record-icon-wrapper">
                        {getRecordIcon(recordType)}
                      </div>
                      <div className="record-title-minimal">
                        <h3>{recordType} Records</h3>
                        <span className="record-count-minimal">{records.length}</span>
                      </div>
                    </div>

                    <div className="record-list-minimal">
                      {records.map((record, index) => (
                        <div key={index} className="record-item-minimal">
                          <div className="record-details-minimal">
                            <div className="record-value-minimal">
                              <span className="record-data-text">{record.data}</span>
                            </div>
                            <div className="record-meta-minimal">
                              <span className="record-name-text">{record.name}</span>
                              <span className="record-ttl-text">TTL: {record.ttl}s</span>
                            </div>
                          </div>
                          <button 
                            onClick={() => copyToClipboard(record.data, `${recordType}-${index}`)}
                            className="copy-record-btn-minimal"
                            title="Copy"
                          >
                            {copied === `${recordType}-${index}` ? <FaCheck /> : <FaCopy />}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Information Section - Minimal */}
      <section className="dns-info-minimal">
        <div className="container">
          <div className="info-content-minimal">
            <h2>Understanding DNS Records</h2>
            <div className="info-grid-minimal">
              <div className="info-card-minimal">
                <div className="info-icon-wrapper">
                  <FaServer className="info-icon-minimal" />
                </div>
                <h4>A & AAAA Records</h4>
                <p>Point your domain to IP addresses</p>
              </div>
              
              <div className="info-card-minimal">
                <div className="info-icon-wrapper">
                  <FaEnvelope className="info-icon-minimal" />
                </div>
                <h4>MX Records</h4>
                <p>Define mail servers for your domain</p>
              </div>
              
              <div className="info-card-minimal">
                <div className="info-icon-wrapper">
                  <FaLink className="info-icon-minimal" />
                </div>
                <h4>CNAME Records</h4>
                <p>Create aliases to other domains</p>
              </div>
              
              <div className="info-card-minimal">
                <div className="info-icon-wrapper">
                  <FaShieldAlt className="info-icon-minimal" />
                </div>
                <h4>TXT Records</h4>
                <p>Store verification and security data</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {copied && (
        <div className="copy-notification">
          <FaCheck className="notification-icon" />
          <span>DNS record copied to clipboard!</span>
        </div>
      )}
    </div>
  );
};

export default DNSRecords;
